import {
  AcceptInvitationForm,
  AgentPresence as ApiAgentPresence,
  AppPresence,
  FreeOrgAdminForm,
  LoginCredentialsOrTokenForm,
  OrgBillingData,
  OrgConfig as ApiOrgConfig,
  OrgInvitationForm,
  StripeOrgAdminForm,
  UnavailabilityReason,
  UpgradeForm,
  UserRole
} from "@closerplatform/spinner-openapi";
import {
  getLanguageOptionWithDefault,
  LanguageOption
} from "@fidget/common/components/form/languagePicker/languagePicker";
import { staticConfig } from "@fidget/common/config/static-config";
import { intl } from "@fidget/common/i18n/TypedIntl";

import { MessageTag } from "@fidget/common/models/messageTag";
import { salesTaxService } from "@fidget/common/services/salesTaxService";
import { logger, LogLevel } from "@fidget/common/utils";
import { getWorkingWeekIntervals, WorkingWeek } from "@fidget/common/utils/getWorkingHours";
import { HttpStatusCode } from "@fidget/common/utils/httpStatusCodes";
import { MINUTE_MILLIS } from "@fidget/common/utils/timeUtils";
import { cast, flow, Instance, SnapshotIn, SnapshotOrInstance, types } from "mobx-state-tree";
import {
  agentGroupsStoreGetter,
  agentStoreGetter,
  calendarStoreGetter,
  messageWidgetsStoreGetter,
  reportsStoreGetter,
  roleStoreGetter,
  servicesGetters,
  topicStoreGetter,
  uiStoreGetter,
  widgetStoreGetter
} from "../utils";
import { CurrentUserEventListener } from "./eventHandlers";
import { OrgConfig } from "./OrgConfig";
import { agentCtxResourceDataToSnapshot, IUserProfile, UserProfile } from "./UserProfile";

export enum ResetPassStatus {
  FINISHED = "FINISHED",
  IN_PROGRESS = "IN_PROGRESS",
  ABORTED = "ABORTED",
  NONE = "NONE"
}

const WorkingWeek = types.array(
  types.model("WorkingInterval", { from: types.number, to: types.number })
);
const SimplifiedUnavailabilityReason = types.model({
  reasonSlug: types.string,
  reason: types.string
});

/**
 * Base CurrentUser store model
 * Responsible for authentication, storing user profile and session state,
 * organization config and updating settings
 */
export const CurrentUserStore = types
  .model("CurrentUserStore", {
    loggedIn: types.maybe(types.boolean),
    dataLoaded: types.maybe(types.boolean),
    profile: types.maybe(UserProfile),
    orgConfig: types.maybe(OrgConfig),
    loginError: types.maybe(types.string),
    loginRedirectPath: types.maybe(types.string),
    logoutError: types.maybe(types.string),
    resetPassStatus: types.optional(
      types.enumeration<ResetPassStatus>("ResetPassStatus", Object.values(ResetPassStatus)),
      ResetPassStatus.NONE
    ),
    passwordError: types.maybe(types.string),
    workingHours: WorkingWeek,
    zoneId: types.maybe(types.string),
    customReasons: types.array(SimplifiedUnavailabilityReason),
    callMaxDuration: types.maybe(types.number),
    planPrice: types.number,
    isReverseTax: types.maybe(types.boolean),
    taxRate: types.maybe(types.number),
    planCurrency: types.maybe(types.string),
    planName: types.maybe(types.string),
    widgetEnabled: types.maybe(types.boolean),
    country: types.maybe(types.string),
    vatId: types.maybe(types.string),
    nextInvoiceAmount: types.maybe(types.number),
    nextInvoiceDate: types.maybe(types.number),
    averageResponseTimeMillis: types.maybe(types.number)
  })
  .views(servicesGetters)
  .views(calendarStoreGetter)
  .views(agentStoreGetter)
  .views(widgetStoreGetter)
  .views(reportsStoreGetter)
  .views(roleStoreGetter)
  .views(messageWidgetsStoreGetter)
  .views(topicStoreGetter)
  .views(agentGroupsStoreGetter)
  .views(uiStoreGetter)
  .views((self) => ({
    /**
     * Presence status
     */
    get status(): AppPresence | undefined {
      return self.profile && self.profile.presence;
    },
    /**
     * User Role
     */
    get role(): UserRole | undefined {
      return self.profile?.role;
    },
    /**
     * User ID
     */
    get id(): string | undefined {
      return self.profile && self.profile.id;
    },
    /**
     * Org ID
     */
    get orgId(): string | undefined {
      return self.profile && self.profile.orgId;
    },

    get avgResponseTimeMillis(): number {
      return self.averageResponseTimeMillis || 0;
    },

    get currentLanguage(): LanguageOption {
      return getLanguageOptionWithDefault(self.orgConfig?.languageLocale);
    },

    get isOauthEnabled(): boolean {
      return !!self.orgConfig?.oauthEnabled;
    }
  }))
  .actions((self) => {
    return {
      loadStoresData() {
        self.messageWidgetsStore.loadMessageWidgets();
        self.topicStore.loadTopics();
      }
    };
  })
  .actions((self) => {
    /**
     * Clear all errors and states associated with authentication / credentials operations
     */
    const clearErrors = function () {
      self.loginError = undefined;
      self.logoutError = undefined;
      self.passwordError = undefined;
    };

    /**
     * Authenticate user with their profile
     * @param {IUserProfile} profile
     */
    const authenticateProfile = flow(function* (profile: SnapshotOrInstance<IUserProfile>) {
      try {
        // load organization configuration
        const orgConfig: ApiOrgConfig = yield self.spinnerService.getOrganizationConfig(
          profile.orgId
        );
        logger.setLevel(LogLevel[orgConfig.agentLogLevel as keyof typeof LogLevel]);
        const orgConfigSnapshot: SnapshotIn<typeof OrgConfig> = { id: profile.orgId, ...orgConfig };

        self.workingHours = getWorkingWeekIntervals(orgConfig.workingHours || []) as IWorkingWeek;
        self.zoneId = orgConfig.zoneId;
        self.calendarStore.setSchedulingDisabled(!self.workingHours.some((day) => !!day.to));
        self.orgConfig = OrgConfig.create(orgConfigSnapshot);
        // FIXME: inappropriate use of cast below, fix types and remove
        self.profile = cast(profile);
        self.loggedIn = true;

        yield self.roleStore.loadRoles();

        yield self.agentStore.createOrgBot(profile.orgId);

        const reasons: UnavailabilityReason[] =
          yield self.spinnerService.getUnavailabilityReasons();
        const mappedReasons = reasons.map((ur) => {
          return { reasonSlug: ur.reasonSlug, reason: ur.reason };
        });
        self.customReasons = cast(mappedReasons);

        const { callMaxDuration } = yield self.spinnerService.getLimits(profile.orgId);
        self.callMaxDuration = callMaxDuration / MINUTE_MILLIS;

        const { price, currency, name } = yield self.spinnerService.getOwnPlan();
        self.planPrice = price;
        self.planCurrency = currency;
        self.planName = name;

        try {
          updateInvoiceInfo();
        } catch (e) {
          if ((e as Response).status !== HttpStatusCode.FORBIDDEN) {
            logger.error({ message: "Couldn't load billing", error: e });
          }
        }

        self.widgetEnabled = false;

        self.reportsStore.prepare();
        yield self.agentGroupsStore.load();

        self.dataLoaded = true;

        self.loadStoresData();

        return self.profile;
      } catch (error) {
        logger.error({ message: "Couldn't authenticate", error });
        throw new Error("Could not load organization config");
      }
    });

    const getTaxRate = flow(function* () {
      try {
        if (self.country) {
          let isVatValid = false;
          if (self.country === "PL") {
            isVatValid = true;
          } else {
            const vatIdForSpinner = self.vatId!.slice(2).trim();
            const spinnerResponse = yield self.spinnerService.checkVat({
              countryCode: self.country,
              vatId: vatIdForSpinner
            });
            isVatValid = spinnerResponse.valid;
          }

          if (self.vatId && isVatValid) {
            const { rate, charge } = yield salesTaxService.getSalesTax(self.country, self.vatId);
            self.isReverseTax = charge.reverse;
            self.taxRate = rate;
          } else {
            const { rate, area } = yield salesTaxService.getSalesTax(self.country);
            if (area === "international" || area === "worldwide") {
              self.taxRate = 0;
            } else {
              self.taxRate = rate;
            }
            self.isReverseTax = false;
          }
        }
      } catch (e) {
        logger.error("Could not check vat id");
      }
    });

    /**
     * Update invoice date and amount
     */
    const updateInvoiceInfo = flow(function* () {
      const result: OrgBillingData = yield self.spinnerService.getCompanyBillingData();
      const { country, vatId, nextInvoiceAmount, nextInvoiceDate } = result;
      self.country = country;
      self.vatId = vatId;
      self.nextInvoiceAmount = nextInvoiceAmount;
      self.nextInvoiceDate = nextInvoiceDate;

      yield getTaxRate();
    });

    /**
     * Trigger first widget rendering
     */
    const enableWidget = () => {
      self.widgetEnabled = true;
    };

    /**
     * De-authenticate current user
     */
    const deauthenticateProfile = function () {
      self.loggedIn = false;
      self.dataLoaded = false;
      self.profile = undefined;
      self.orgConfig = undefined; // XXX: possibly keep the org config after signing out?
      logger.clearUserLogContext();
    };

    /**
     * Get current user data from backend
     */
    const loadProfileData = flow(function* () {
      try {
        const [session, _] = yield Promise.all([
          self.spinnerService.getSession(),
          self.agentGroupsStore.load()
        ]);
        const profile = agentCtxResourceDataToSnapshot(session) as SnapshotIn<IUserProfile>;
        logger.setUserLogContext({ orgId: profile.orgId, userId: profile.id });

        self.profile = cast(profile);
      } catch (response) {
        logger.error("Couldn't update session information", response);
        self.dataLoaded = false;
      }
    });

    /**
     * Sign in to backend
     * @param {LoginCredentialsOrTokenForm | undefined} credentials  Login credentials
     */
    const login = flow(function* (credentials?: LoginCredentialsOrTokenForm) {
      try {
        self.loginError = undefined;

        const resource = yield credentials
          ? self.spinnerService.login(credentials)
          : self.spinnerService.getSession();
        const profile = agentCtxResourceDataToSnapshot(resource) as SnapshotIn<IUserProfile>;
        yield authenticateProfile(profile);
      } catch (response) {
        logger.error("Couldn't log in", response);
        self.loggedIn = false;
        self.dataLoaded = false;
        // be quiet about errors when there are no credentials (session cookie login case)
        const res: { message?: string; } = yield (response as Response).json();
        self.loginError = credentials ? res.message : undefined;
      }
    });

    /**
     * Sign out from backend
     */
    const logout = flow(function* () {
      try {
        deauthenticateProfile();
        yield self.spinnerService.logout();
        window.location.reload();
      } catch (response) {
        self.loggedIn = false;
        self.dataLoaded = false;
        const { message }: { message?: string; } = yield (response as Response).json();
        self.logoutError = message;
        throw new Error(self.logoutError);
      }
    });

    /**
     * Request sending of password reset link
     * @param {string} email  user's stated email address
     */
    const requestPasswordResetLink = flow(function* (email: string) {
      try {
        self.loginError = undefined;

        yield self.spinnerService.resetPassword({ email });
      } catch (response) {
        if ((response as Response).status === HttpStatusCode.FORBIDDEN) {
          self.uiStore.addErrorToast(intl.formatMessage("login.password.forgotPassword.forbidden"));
        }
        logger.error("Couldn't request password reset", response);
        const { message }: { message?: string; } = yield (response as Response).json();
        throw Error(message);
      }
    });

    /**
     * Change password with token as part of the password reset flow
     * @param {string} token  token from the reset link
     * @param {string} newPassword  password to change to
     */
    const changePasswordWithToken = flow(function* (token: string, newPassword: string) {
      try {
        self.passwordError = undefined;

        yield self.spinnerService.changePasswordWithToken({ newPassword, token });
        self.resetPassStatus = ResetPassStatus.FINISHED;
      } catch (response) {
        const { message }: { message?: string; } = yield (response as Response).json();
        self.passwordError = message;
        throw self.passwordError;
      }
    });

    /**
     * Change password by authenticating with current password
     * @param {string} oldPassword  current password
     * @param {string} newPassword  password to change to
     */
    const changePasswordWithCurrentPassword = flow(function* (
      oldPassword: string,
      newPassword: string
    ) {
      try {
        self.loginError = undefined;
        self.passwordError = undefined;

        yield self.spinnerService.changePassword({ newPassword, oldPassword });
      } catch (err) {
        const { message }: { message?: string; } = yield (err as Response).json();

        if ((err as Response).status === HttpStatusCode.UNAUTHORIZED) {
          self.loginError = message;
        } else {
          self.passwordError = message;
        }
        throw Error(message);
      }
    });

    /**
     * Set reset pass flow status
     * @param {ResetPassStatus} status
     */
    const setResetPassStatus = (status: ResetPassStatus) => {
      self.resetPassStatus = status;
    };

    /**
     * Request login via magic link
     * TODO: handle request error in respective components
     * @param {string} email  user's stated email address
     */
    const requestMagicLinkLogin = flow(function* (email: string) {
      try {
        yield self.spinnerService.magiclink({ email });
      } catch (response) {
        if ((response as Response).status === HttpStatusCode.FORBIDDEN) {
          self.uiStore.addErrorToast(intl.formatMessage("login.magicLink.forbidden"));
        }
        throw Error("Could not request magic link");
      }
    });

    /**
     * Accept invitation
     * @param {string} token
     */
    const acceptInvitation = flow(function* (token: string, data: AcceptInvitationForm) {
      try {
        const resource = yield self.spinnerService.acceptInvitation(token, data);
        const profile = agentCtxResourceDataToSnapshot(resource);
        return profile;
      } catch (response) {
        throw Error("Could not login via invitation");
      }
    });

    /**
     * Create stripe org admin
     * @param {string} token
     */
    const createStripeOrgAdmin = flow(function* (token: string, data: StripeOrgAdminForm) {
      try {
        return yield self.spinnerService.createStripeOrgAdmin(token, data);
      } catch (error) {
        logger.error({ message: "Couldn't create billing admin", error });
        throw Error("Could not create org");
      }
    });
    /**
     * Upgrade org plan with stripe
     */
    const upgradeOrg = flow(function* (data: UpgradeForm) {
      try {
        return yield self.spinnerService.upgradeOrg(data);
      } catch (error) {
        logger.error({ message: "Couldn't upgrade org", error });
        throw Error("Error while upgrading org");
      }
    });
    /**
     * Create free org admin
     * @param {string} token
     */
    const createFreeOrgAdmin = flow(function* (token: string, data: FreeOrgAdminForm) {
      try {
        const resource = yield self.spinnerService.createFreeOrgAdmin(token, data);
        const profile = agentCtxResourceDataToSnapshot(resource);
        return profile;
      } catch (error) {
        logger.error({ message: "Couldn't create free org admin", error });
        throw Error("Could not create org");
      }
    });

    /**
     * Check user invitation token and return invitee data
     */
    const verifyUserInvitationByToken = flow(function* (token: string) {
      return yield self.spinnerService.getTokenValidity(token);
    });

    /**
     * Check org invitation token and return invitee data
     */
    const verifyOrgInvitationByToken = flow(function* (token: string) {
      return yield self.spinnerService.getOrgTokenValidity(token);
    });

    /**
     * Signup organization with email
     */
    const signupOrg = flow(function* (data: OrgInvitationForm, plan: string) {
      try {
        yield self.spinnerService.inviteOrg(plan, data);
        self.widgetStore.updateUserDetails({ email: data.email, languageLocale: data.languageTag });
        return true;
      } catch (reason) {
        const { message }: { message?: string; } = yield (reason as Response).json();
        if ((reason as Response).status === HttpStatusCode.CONFLICT) {
          throw { email: message };
        }
        throw "Could not sign up organization";
      }
    });

    /**
     * Set current user's presence
     * XXX: should it throw error when profile is not present?
     * @param {PresenceStatus} presence  presence status
     */
    const setPresence = flow(function* (
      presence: AppPresence,
      persist: boolean,
      unavailabilityReason?: string
    ) {
      try {
        if (
          self.profile &&
          (self.profile.presence !== presence ||
            self.profile.unavailabilityReason !== unavailabilityReason)
        ) {
          if (persist) {
            yield self.spinnerService.setAgentPresence({
              presence,
              reason: unavailabilityReason
            } as ApiAgentPresence);
          }
          self.profile.presence = presence;
          self.profile.unavailabilityReason = unavailabilityReason;
        }
      } catch (reason) {
        throw Error("Could not set user's presence");
      }
    });

    /**
     * Set path to redirect to after login
     * @param {string | undefined}  path
     */
    const setLoginRedirectPath = (path: string | undefined) => {
      self.loginRedirectPath = path;
    };

    const setWorkingHours = flow(function* (workingWeek: WorkingWeek) {
      if (self.orgConfig) {
        try {
          yield self.orgConfig.updateWorkingHours(workingWeek);
          self.workingHours = cast(workingWeek);
          self.calendarStore.setSchedulingDisabled(!workingWeek.some((day) => !!day.to));
        } catch (e) {
          const { message }: { message?: string; } = yield (e as Response).json();
          throw Error(message);
        }
      }
    });

    const reloadAverageResponseTime = flow(function* () {
      try {
        const now = new Date();
        const fromTimestamp = now.setHours(now.getHours() - staticConfig.avgResponseHoursRange);
        const toTimestamp = Date.now();
        if (self.id) {
          const maybeResult = yield self.spinnerService.getAverageResponseTime(
            fromTimestamp,
            toTimestamp
          );
          self.averageResponseTimeMillis = maybeResult.avgResponseTimeMillis;
        } else {
          self.averageResponseTimeMillis = 0;
        }
      } catch (e) {
        self.averageResponseTimeMillis = 0;
      }
    });

    const sendCobrowsingProposalToRoom = flow(function* (roomId: string) {
      const message = intl.formatMessage("richMessages.cobrowsingProposal.message", {
        user: self.profile?.firstName
      });
      yield self.artichokeService.sendCustom(roomId, message, MessageTag.COBROWSING_PROPOSAL, {});
    });

    return {
      clearErrors,
      authenticateProfile,
      deauthenticateProfile,
      enableWidget,
      updateInvoiceInfo,
      getTaxRate,
      loadProfileData,
      login,
      logout,
      requestPasswordResetLink,
      changePasswordWithToken,
      changePasswordWithCurrentPassword,
      setResetPassStatus,
      acceptInvitation,
      createStripeOrgAdmin,
      createFreeOrgAdmin,
      upgradeOrg,
      verifyUserInvitationByToken,
      verifyOrgInvitationByToken,
      signupOrg,
      requestMagicLinkLogin,
      setPresence,
      setLoginRedirectPath,
      setWorkingHours,
      reloadAverageResponseTime,
      sendCobrowsingProposalToRoom
    };
  })
  .actions((self) => {
    const eventListenerName = "CurrentUserEventListener";

    const afterCreate = () => {
      self.eventListener.addGlobalListener(
        new CurrentUserEventListener(() => self as ICurrentUserStore, eventListenerName)
      );
    };

    const beforeDestroy = () => {
      self.eventListener.removeGlobalListener(eventListenerName);
    };

    return {
      afterCreate,
      beforeDestroy
    };
  });

export type IWorkingWeek = Instance<typeof WorkingWeek>;
export interface ICurrentUserStore extends Instance<typeof CurrentUserStore> { }
